/**
 * TimelineScheduler — Fluent DSL helper for building timeline entries.
 *
 * Usage:
 * ```
 * createAbility('meteor')
 *   .withTimeline(2000)
 *   .atTime(0).playVFX('meteor_charge')
 *   .atTime(1500).playVFX('meteor_impact')
 *   .atTime(1500).screenShake(15, 500)
 *   .build();
 * ```
 */

import type { ActionContext } from '../types';
import type { ProjectileConfig } from '../AbilityBuilder';
import type { TimelineEntry } from './AbilityTimeline';

export interface TimelineBuilderHost {
  _timelineEntries: TimelineEntry[];
  atTime(ms: number): TimelineScheduler;
}

export class TimelineScheduler {
  constructor(
    private host: TimelineBuilderHost,
    private timeMs: number,
  ) {}

  /** Schedule a VFX effect at this time offset */
  playVFX(effectId: string, offset?: { x: number; y: number }): TimelineBuilderHost & { atTime(ms: number): TimelineScheduler } {
    this.host._timelineEntries.push({
      at: this.timeMs,
      action: { type: 'vfx', effectId, offset },
    });
    return this.host as any;
  }

  /** Schedule a projectile spawn at this time offset */
  spawnProjectile(config?: Partial<ProjectileConfig>): TimelineBuilderHost & { atTime(ms: number): TimelineScheduler } {
    this.host._timelineEntries.push({
      at: this.timeMs,
      action: { type: 'projectile', config },
    });
    return this.host as any;
  }

  /** Schedule a screen shake at this time offset */
  screenShake(intensity: number, duration: number): TimelineBuilderHost & { atTime(ms: number): TimelineScheduler } {
    this.host._timelineEntries.push({
      at: this.timeMs,
      action: { type: 'screenShake', intensity, duration },
    });
    return this.host as any;
  }

  /** Schedule a screen flash at this time offset */
  screenFlash(color: number, duration: number): TimelineBuilderHost & { atTime(ms: number): TimelineScheduler } {
    this.host._timelineEntries.push({
      at: this.timeMs,
      action: { type: 'screenFlash', color, duration },
    });
    return this.host as any;
  }

  /** Schedule a custom callback at this time offset */
  callback(fn: (ctx: ActionContext) => void): TimelineBuilderHost & { atTime(ms: number): TimelineScheduler } {
    this.host._timelineEntries.push({
      at: this.timeMs,
      action: { type: 'callback', fn },
    });
    return this.host as any;
  }

  /** Schedule casting another registered ability at this time offset */
  castAbility(abilityId: string, damageMultiplier?: number): TimelineBuilderHost & { atTime(ms: number): TimelineScheduler } {
    this.host._timelineEntries.push({
      at: this.timeMs,
      action: { type: 'ability', abilityId, damageMultiplier },
    });
    return this.host as any;
  }
}
